
import { useState, useEffect, useRef, memo } from 'react';
import { LazyExoticComponent, ComponentType } from 'react';
import LazyLoadedSection from './LazyLoadedSection';

interface OptimizedSectionLoaderProps {
  Component: LazyExoticComponent<ComponentType<any>>;
  placeholderBg: string;
  placeholderHeight: string;
  animation?: "fade" | "slide";
  style?: React.CSSProperties;
  priority?: 'high' | 'medium' | 'low';
}

const OptimizedSectionLoader = memo(({
  Component,
  placeholderBg,
  placeholderHeight,
  animation = "slide",
  style,
  priority = 'medium'
}: OptimizedSectionLoaderProps) => {
  const [shouldLoad, setShouldLoad] = useState(priority === 'high');
  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldLoad || priority === 'high') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: priority === 'low' ? '200px' : '500px',
        threshold: 0.1
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [shouldLoad, priority]);

  if (!shouldLoad) {
    return (
      <div 
        ref={observerRef}
        className={`${placeholderHeight} ${placeholderBg} animate-pulse`}
        style={style}
      />
    );
  }

  return (
    <LazyLoadedSection
      Component={Component}
      placeholderBg={placeholderBg}
      placeholderHeight={placeholderHeight}
      animation={animation}
      style={style}
    />
  );
});

OptimizedSectionLoader.displayName = 'OptimizedSectionLoader';

export default OptimizedSectionLoader;
